import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faEye, 
  faEdit, 
  faTrashAlt,
  faFilter,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { Beneficiary } from '../../interfaces/beneficiary';
import { getBeneficiaries, deleteBeneficiary, searchBeneficiaries } from '../../services/beneficiaryService';
import './BeneficiaryList.css';

const BeneficiaryList: React.FC = () => {
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    nationality: '',
    hasPhysicalDni: ''
  });

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      setLoading(true);
      const data = await getBeneficiaries();
      setBeneficiaries(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching beneficiaries:', err);
      setError('No se pudieron cargar los beneficiarios. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchBeneficiaries();
      return;
    }
    
    try {
      setLoading(true);
      const data = await searchBeneficiaries(searchTerm);
      setBeneficiaries(data);
    } catch (err) {
      console.error('Error searching beneficiaries:', err);
      setError('No se pudo realizar la búsqueda. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number | undefined) => {
    if (!id) return;
    
    if (window.confirm('¿Está seguro de que desea eliminar este beneficiario? Esta acción no se puede deshacer.')) {
      try {
        await deleteBeneficiary(id);
        // Actualizar la lista de beneficiarios
        setBeneficiaries(beneficiaries.filter(b => b.id !== id));
      } catch (err) {
        console.error('Error deleting beneficiary:', err);
        setError('No se pudo eliminar el beneficiario. Por favor, intente nuevamente.');
      }
    }
  };

  // Formatea el nombre completo del beneficiario
  const formatName = (beneficiary: Beneficiary) => {
    const { first_name, last_name, nickname } = beneficiary;
    let formattedName = [first_name, last_name].filter(Boolean).join(' ');
    if (nickname) {
      formattedName += ` '${nickname}'`;
    }
    return formattedName || 'Sin nombre';
  };

  return (
    <div className="beneficiary-list-container">
      <div className="flex justify-between align-center mb-4">
        <h1>Beneficiarios</h1>
        <Link to="/beneficiaries/create" className="btn btn-primary">
          <FontAwesomeIcon icon={faPlus} /> Nuevo Beneficiario
        </Link>
      </div>

      <div className="filters card mb-4">
        <h3 className="mb-2">Filtros</h3>
        <div className="filter-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Nombre/DNI/Apodo</label>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, DNI o apodo"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Nacionalidad</label>
            <select
              className="form-control"
              value={filters.nationality}
              onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
            >
              <option value="">Todas las nacionalidades</option>
              <option value="Argentina">Argentina</option>
              <option value="Bolivia">Bolivia</option>
              <option value="Paraguay">Paraguay</option>
              <option value="Perú">Perú</option>
              <option value="Venezuela">Venezuela</option>
              <option value="Colombia">Colombia</option>
              <option value="Otra">Otra</option>
            </select>
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">DNI Físico</label>
            <select
              className="form-control"
              value={filters.hasPhysicalDni}
              onChange={(e) => setFilters({ ...filters, hasPhysicalDni: e.target.value })}
            >
              <option value="">Todos</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        <div className="flex justify-between">
          <button className="btn btn-secondary">
            <FontAwesomeIcon icon={faFilter} /> Más filtros
          </button>
          <button className="btn btn-primary" onClick={handleSearch}>
            <FontAwesomeIcon icon={faSearch} /> Buscar
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="text-center p-4">Cargando beneficiarios...</div>
        ) : beneficiaries.length === 0 ? (
          <div className="text-center p-4">No se encontraron beneficiarios.</div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>DNI</th>
                  <th>Contacto</th>
                  <th>Ubicación</th>
                  <th>Nacionalidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {beneficiaries.map((beneficiary) => (
                  <tr key={beneficiary.id}>
                    <td>{formatName(beneficiary)}</td>
                    <td>
                      {beneficiary.dni}
                      {beneficiary.has_physical_dni && (
                        <span className="badge badge-success ml-2" title="Tiene DNI físico">
                          DNI
                        </span>
                      )}
                    </td>
                    <td>{beneficiary.phone_number || 'No registrado'}</td>
                    <td>{beneficiary.address || 'No registrada'}</td>
                    <td>{beneficiary.nationality || 'No registrada'}</td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/beneficiaries/${beneficiary.id}`} 
                          className="icon-button" 
                          title="Ver detalles"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/beneficiaries/${beneficiary.id}/edit`} 
                          className="icon-button" 
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button
                          className="icon-button"
                          title="Eliminar"
                          onClick={() => handleDelete(beneficiary.id)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between align-center mt-4">
        <div>
          Mostrando {beneficiaries.length} beneficiarios
        </div>
        {/* Paginación - se implementará cuando sea necesario */}
        <ul className="pagination">
          <li><a href="#"><FontAwesomeIcon icon={faChevronLeft} /></a></li>
          <li><a href="#" className="active">1</a></li>
          <li><a href="#">2</a></li>
          <li><a href="#"><FontAwesomeIcon icon={faChevronRight} /></a></li>
        </ul>
      </div>
    </div>
  );
};

export default BeneficiaryList; 